import React, { useState, useRef, useEffect } from 'react';
import { config } from "../Const.js";
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { executeFetchAsync } from '../Util.js';

function SearchBox({ value, endPoint, onEntitySelected, resultMappingFunction, defaultText, small }) {
    const [options, setOptions] = useState([]);
    const searchLabel = defaultText ?? "Search Entity";
    const previousController = useRef(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        return () => {
            if (previousController.current) {
                previousController.current.abort();
            }
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, []);

    const onInputChange = (event, inputValue, reason) => {
        if (!inputValue) {
            setOptions([]);
            if (previousController.current) {
                previousController.current.abort();
                previousController.current = null;
            }
            return;
        }

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            getData(inputValue);
        }, 250);
    };

    const getData = async (searchTerm) => {
        if (previousController.current) {
            previousController.current.abort();
        }

        const controller = new AbortController();
        const { signal } = controller;
        previousController.current = controller;

        const encoded = encodeURIComponent(searchTerm);
        const url = `${config.SERVER_URL}/api/v1/admin/search/${endPoint}?q=${encoded}`;
        const headers = {
            "Content-Type": "application/json",
            Accept: "application/json"
        };

        try {
            const results = await executeFetchAsync(url, "get", headers, null, signal);

            if (!results || !Array.isArray(results)) return;
            if (previousController.current !== controller) return;

            const updatedOptions = results.map(resultMappingFunction);
            setOptions(updatedOptions);
        } catch (err) {
            if (err?.name === 'AbortError') return;
            console.error('SearchBox getData error:', err);
        } finally {
            if (previousController.current === controller) {
                previousController.current = null;
            }
        }
    };

    return (
        <Autocomplete
            sx={{ mt: '20px', width: '100%' }}
            // Controlled component props
            value={value ?? null}
            options={options}
            // Prevents MUI from hiding the selected value when options are empty
            filterOptions={(x) => x}
            onInputChange={onInputChange}
            isOptionEqualToValue={(option, value) => (option?.id ?? null) === (value?.id ?? null)}
            onChange={(e, result) => onEntitySelected(result)}
            getOptionLabel={(option) => option?.label ?? ''}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={searchLabel}
                    variant="outlined"
                    size={small ? "small" : "medium"}
                />
            )}
            renderOption={(props, option) => (
                <li {...props} key={option.id}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        {option.label}
                        <Typography variant="body2" color="purple">
                            {option.type?.toLowerCase()}
                        </Typography>
                    </Box>
                </li>
            )}
        />
    );
}

export default SearchBox;